import { type ReactNode, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Tag,
  formatDicomDate,
  formatDicomTime,
  formatPersonName,
  getCodeMeaning,
  getNumber,
  getPersonName,
  getSequence,
  getString,
  getStrings,
  patientAge,
  type DicomJson,
} from '../../data/dicomTags';
import { uidName } from '../../data/uidNames';
import { SopClass } from '../../viewer/sopClasses';
import { isRadiograph, readout, windowingOf } from '../../viewer/window/windowing';

interface Props {
  metadata: DicomJson[];
}

/** A coded UID shown by its standard name (falls back to the raw UID if unknown),
 *  with the raw UID revealed on hover and copied on click. */
function CodedUid({ uid }: { uid: string }) {
  const [copied, setCopied] = useState(false);
  const name = uidName(uid);
  const copy = () =>
    navigator.clipboard?.writeText(uid).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      },
      () => {},
    );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={copy} className="text-left underline-offset-2 hover:underline">
          {name ?? uid}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs">
        <div className="break-all font-mono text-xs">{uid}</div>
        <div className="mt-1 text-[10px] text-muted-foreground">
          {copied ? 'Copied' : 'Click to copy'}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// Pick the base (highest-resolution) WSI level: the instance with the largest
// total pixel matrix, so the panel reflects full-resolution geometry.
function baseInstance(metadata: DicomJson[]): DicomJson | undefined {
  let best: DicomJson | undefined;
  let bestArea = -1;
  for (const ds of metadata) {
    const area =
      (getNumber(ds, Tag.TotalPixelMatrixColumns) ?? getNumber(ds, Tag.Columns) ?? 0) *
      (getNumber(ds, Tag.TotalPixelMatrixRows) ?? getNumber(ds, Tag.Rows) ?? 0);
    if (area > bestArea) {
      bestArea = area;
      best = ds;
    }
  }
  return best ?? metadata[0];
}

function Section({ title, rows }: { title: string; rows: [string, ReactNode][] }) {
  const present = rows.filter(([, v]) => v != null && v !== '');
  if (present.length === 0) return null;
  return (
    <div>
      <h6 className="mb-1 text-sm font-semibold">{title}</h6>
      <dl className="grid grid-cols-[45%_55%] gap-x-2 gap-y-0.5 text-xs">
        {present.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** "PERIPHERAL EQUALIZATION" → "Peripheral equalization": DX code strings as words. */
const asWords = (v: string | undefined) => (v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : undefined);
const withUnit = (v: number | undefined, unit: string) => (v == null ? undefined : `${v.toLocaleString()} ${unit}`);
/** A positive value with its unit; a 0 is a field the device left unset (a Faxitron writes 0 mA beside 400 µA). */
const measured = (v: number | undefined, unit: string) => (v != null && v > 0 ? withUnit(v, unit) : undefined);
/** ImageLaterality in words; U (an unpaired body part) says nothing about a specimen. */
const LATERALITY: Record<string, string> = { L: 'Left', R: 'Right', B: 'Both' };

/** Where an X-ray's spacing comes from, as the converter wrote it (convert sources/radiograph.py). */
const SPACING_BASIS: Record<string, string> = {
  calibrated: 'calibrated',
  detector: 'at the detector',
  'magnification-corrected': 'corrected for magnification',
  unknown: 'unknown',
};

// An X-ray's acquisition, carried onto its pyramid by the converter: how it was
// exposed, how far the source stood, how its pixels measure, and the window the
// device chose, in the device's own numbers.
function XraySection({ ds }: { ds: DicomJson }) {
  const windowing = windowingOf(ds);
  const device = windowing?.presets[0];
  const microAs = getNumber(ds, Tag.ExposureInuAs);
  const mAs = getNumber(ds, Tag.Exposure);
  const spacing = getStrings(getSequence(getSequence(ds, Tag.SharedFunctionalGroupsSequence)[0], Tag.PixelMeasuresSequence)[0], Tag.PixelSpacing)[0];
  const basis = /spacing ([a-z-]+)/.exec(getString(ds, Tag.DerivationDescription) ?? '')?.[1];
  const raw = windowing?.forProcessing;
  return (
    <Section
      title="X-ray"
      rows={[
        ['Presentation', raw ? 'For processing: raw detector data, opened on its full range' : asWords(getString(ds, Tag.PresentationIntentType))],
        ['Post-processing', asWords(getString(ds, Tag.PostprocessingFunction))],
        ['Tube voltage', measured(getNumber(ds, Tag.KVP), 'kVp')],
        ['Exposure', measured(microAs, 'µAs') ?? measured(mAs, 'mAs')],
        ['Exposure time', measured(getNumber(ds, Tag.ExposureTime), 'ms')],
        ['Tube current', measured(getNumber(ds, Tag.XRayTubeCurrentInuA), 'µA') ?? measured(getNumber(ds, Tag.XRayTubeCurrent), 'mA')],
        ['Source to detector', withUnit(getNumber(ds, Tag.DistanceSourceToDetector), 'mm')],
        ['Source to object', withUnit(getNumber(ds, Tag.DistanceSourceToPatient), 'mm')],
        ['Magnification', getNumber(ds, Tag.EstimatedRadiographicMagnificationFactor)?.toString()],
        ['Pixel spacing', spacing ? `${Number(spacing)} mm${basis && SPACING_BASIS[basis] ? ` (${SPACING_BASIS[basis]})` : ''}` : undefined],
        ['Detector', asWords(getString(ds, Tag.DetectorType))],
        ['Bits stored', numOrUndef(getNumber(ds, Tag.BitsStored))],
        ['View', getString(ds, Tag.ViewPosition)],
        ['Laterality', LATERALITY[getString(ds, Tag.ImageLaterality) ?? '']],
        ['Body part', asWords(getString(ds, Tag.BodyPartExamined))],
        ['Device window', device && windowing ? readout(device.window, windowing) : undefined],
      ]}
    />
  );
}

// Specimen / Container module: container-level identifiers plus one block of
// rows per specimen in the Specimen Description Sequence. Hidden when empty.
function SpecimenSection({ ds }: { ds: DicomJson }) {
  const containerIssuer = getString(
    getSequence(ds, Tag.IssuerOfTheContainerIdentifierSequence)[0],
    Tag.LocalNamespaceEntityID,
  );
  const specimens = getSequence(ds, Tag.SpecimenDescriptionSequence);
  const multi = specimens.length > 1;

  const rows: [string, ReactNode][] = [
    ['Container ID', getString(ds, Tag.ContainerIdentifier)],
    ['Container issuer', containerIssuer],
  ];
  specimens.forEach((sp, i) => {
    const p = multi ? `Specimen ${i + 1} ` : '';
    rows.push(
      [`${p}ID`, getString(sp, Tag.SpecimenIdentifier)],
      [`${p}UID`, getString(sp, Tag.SpecimenUID)],
      [`${p}Description`, getString(sp, Tag.SpecimenShortDescription)],
      [`${p}Type`, getCodeMeaning(sp, Tag.SpecimenTypeCodeSequence)],
      [`${p}Anatomic site`, getCodeMeaning(sp, Tag.PrimaryAnatomicStructureSequence)],
      [`${p}Stain`, stainOf(sp)],
    );
  });

  return <Section title="Specimen" rows={rows} />;
}

/** The substance of the staining step in the Specimen Preparation Sequence
 *  (TID 8001: a CODE item named "Using substance", 424361007). */
function stainOf(sp: DicomJson): string | undefined {
  for (const step of getSequence(sp, Tag.SpecimenPreparationSequence)) {
    for (const item of getSequence(step, Tag.SpecimenPreparationStepContentItemSequence)) {
      const name = getSequence(item, Tag.ConceptNameCodeSequence)[0];
      if (name && getString(name, Tag.CodeValue) === '424361007') {
        return getCodeMeaning(item, Tag.ConceptCodeSequence);
      }
    }
  }
  return undefined;
}

/** Objective power and illumination, from the first optical path (WSI). */
function OpticalPathSection({ ds }: { ds: DicomJson }) {
  const path = getSequence(ds, Tag.OpticalPathSequence)[0];
  if (!path) return null;
  const power = getString(path, Tag.ObjectiveLensPower);
  return (
    <Section
      title="Optical path"
      rows={[
        ['Objective power', power ? `${Number(power)}x` : undefined],
        ['Illumination', getCodeMeaning(path, Tag.IlluminationTypeCodeSequence)],
      ]}
    />
  );
}

export function MetadataPanel({ metadata }: Props) {
  const ds = baseInstance(metadata);
  if (!ds) {
    return <div className="p-3 text-sm text-muted-foreground">No metadata.</div>;
  }

  const pixelSpacing = getStrings(ds, Tag.PixelSpacing);
  const sopClassUid = getString(ds, Tag.SOPClassUID);
  const transferSyntaxUid = getString(ds, Tag.TransferSyntaxUID);
  // An X-ray is stored as a pyramid (convert sources/radiograph.py) but is no slide: its own section says so.
  const radiograph = isRadiograph(ds);
  const isWsi = sopClassUid === SopClass.VLWholeSlideMicroscopyImage && !radiograph;

  const age = patientAge(ds);
  const birthDate = formatDicomDate(getString(ds, Tag.PatientBirthDate));
  const birthDateDisplay = birthDate ? (age ? `${birthDate} (${age})` : birthDate) : undefined;

  return (
    <div className="h-full overflow-y-auto p-3">
      <div className="flex flex-col gap-4">
        <Section
          title="Patient"
          rows={[
            ['Name', formatPersonName(getPersonName(ds, Tag.PatientName))],
            ['ID', getString(ds, Tag.PatientID)],
            ['ID issuer', getString(ds, Tag.IssuerOfPatientID)],
            ['Birth date', birthDateDisplay],
            ['Age', birthDate ? undefined : age],
            ['Sex', getString(ds, Tag.PatientSex)],
          ]}
        />
        <Section
          title="Study"
          rows={[
            ['Date', formatDicomDate(getString(ds, Tag.StudyDate))],
            ['Time', formatDicomTime(getString(ds, Tag.StudyTime))],
            ['Study ID', getString(ds, Tag.StudyID)],
            ['Accession', getString(ds, Tag.AccessionNumber)],
            ['Description', getString(ds, Tag.StudyDescription)],
            ['Diagnosis', getString(ds, Tag.AdmittingDiagnosesDescription)],
            ['Referring physician', formatPersonName(getPersonName(ds, Tag.ReferringPhysicianName))],
            ['Procedure', getCodeMeaning(getSequence(ds, Tag.RequestAttributesSequence)[0], Tag.RequestedProcedureCodeSequence)],
          ]}
        />
        <Section
          title="Series"
          rows={[
            ['Modality', getString(ds, Tag.Modality)],
            ['Description', getString(ds, Tag.SeriesDescription)],
            ['Date', formatDicomDate(getString(ds, Tag.SeriesDate))],
            ['Time', formatDicomTime(getString(ds, Tag.SeriesTime))],
            ['SOP Class', sopClassUid ? <CodedUid uid={sopClassUid} /> : undefined],
            ['Transfer syntax', transferSyntaxUid ? <CodedUid uid={transferSyntaxUid} /> : undefined],
          ]}
        />
        <SpecimenSection ds={ds} />
        {/* After the specimen it is a picture of (user, 2026-10-16). */}
        {isRadiograph(ds) && <XraySection ds={ds} />}
        {!isRadiograph(ds) && <OpticalPathSection ds={ds} />}
        <Section
          title="Equipment"
          rows={[
            ['Manufacturer', getString(ds, Tag.Manufacturer)],
            ['Model', getString(ds, Tag.ManufacturerModelName)],
            ['Serial number', getString(ds, Tag.DeviceSerialNumber)],
            ['Software', getStrings(ds, Tag.SoftwareVersions).join(', ') || undefined],
            ['Institution', getString(ds, Tag.InstitutionName)],
            ['Station', getString(ds, Tag.StationName)],
          ]}
        />
        {isWsi ? (
          <Section
            title="Whole-slide image"
            rows={[
              [
                'Total pixel matrix',
                fmtMatrix(
                  getNumber(ds, Tag.TotalPixelMatrixColumns),
                  getNumber(ds, Tag.TotalPixelMatrixRows),
                ),
              ],
              ['Tile size', fmtMatrix(getNumber(ds, Tag.Columns), getNumber(ds, Tag.Rows))],
              ['Pyramid levels', String(metadata.length)],
              ['Frames (base level)', numOrUndef(getNumber(ds, Tag.NumberOfFrames))],
              ['Pixel spacing (mm)', pixelSpacing.length ? pixelSpacing.join(' \\ ') : undefined],
              ['Frame of Reference', getString(ds, Tag.FrameOfReferenceUID)],
            ]}
          />
        ) : (
          <Section
            title="Image"
            rows={
              radiograph
                ? [
                    ['Dimensions', fmtMatrix(getNumber(ds, Tag.TotalPixelMatrixColumns), getNumber(ds, Tag.TotalPixelMatrixRows))],
                    ['Levels', String(metadata.length)],
                  ]
                : [
                    ['Dimensions', fmtMatrix(getNumber(ds, Tag.Columns), getNumber(ds, Tag.Rows))],
                    ['Frames', numOrUndef(getNumber(ds, Tag.NumberOfFrames))],
                    ['Pixel spacing (mm)', pixelSpacing.length ? pixelSpacing.join(' \\ ') : undefined],
                  ]
            }
          />
        )}
      </div>
    </div>
  );
}

function fmtMatrix(cols?: number, rows?: number): string | undefined {
  if (cols == null || rows == null) return undefined;
  return `${cols} × ${rows}`;
}

function numOrUndef(n?: number): string | undefined {
  return n == null ? undefined : n.toLocaleString();
}
